//CPE: Claim Processing Engine

import Axios, { AxiosInstance, AxiosStatic } from 'axios';
import { OcrProcessingData } from './interfaces';

const CPE_API = process.env.REACT_APP_CPE_API;
const CPE_API_KEY = process.env.REACT_APP_CPE_API_KEY;

interface ClaimResponse {
	id: string;
	policyId: string;
	claimNumber: string;
	claimType: string;
	company: string;
	companyId: string;
	driverId: string;
	insuredIndividualId: string;
	driverRelationshipToInsured: string;
	claimFiler: string;
	claimBeneficiary: string;
	createdAt: string;
	updatedAt: string;
	completedAt: string;
	currentAgentId: string;
	status: string;
	currentStage: string;
	policyType: string;
	additionalNotes: string;
	poNumber: string;
	appliesTo: string;
}

interface ClaimInspectionResponse {
	id: string;
	claimId: string;
}

interface StatusFiles {
	uploaded: string[];
	pending: string[];
}

interface ClaimDocumentStatusResponse {
	vehicleImage: StatusFiles;
	documents: StatusFiles;
}

interface ClaimAdditionalDocumentsResponse {
	id: string;
	claimId: string;
	documentType: string;
}

export interface CategoryLicensePlateResults {
	detection: {
		detected: boolean;
		boundingBox: object | null;
		confidence: number | null;
	};
	licensePlateNumber: string | null;
	metadata: {
		averageOcrConfidence: number;
		processedAt: string;
		timings: {
			imageProcessing: number;
			objectDetection: number;
			imageAugmentation: number;
			imageCropping: number;
			ocrProcessing: number;
			textAnalysis: number;
			totalProcessingTime: number;
		};
	};
}

export interface CategoryDriverLicenseResults {
	licenseNumber: string | null;
	fullName: string | null;
	dateOfBirth: string | null;
	residentialAddress: string | null;
	issueDate: string | null;
	expirationDate: string | null;
	issuingTerritory: string | null;
	metadata: {
		averageOcrConfidence: number;
		processedAt: string;
		timings: {
			imageProcessing: number;
			objectDetection: number;
			imageCropping: number;
			imageAugmentation: number;
			ocrProcessing: number;
			bedrockAnalysis: number;
			totalProcessingTime: number;
		};
	};
}

export interface CategoryVehicleResults {
	angle: string; // Detected vehicle angle
	probability: number; // Model confidence (0-100)
	metadata: {
		processedAt: string;
		timings: {
			imageProcessing: number;
			predictionProcessing: number;
			totalProcessingTime: number;
		};
	};
}

export interface CategoryVINResults {
	vinNumber: string | null;
	metadata: {
		averageOcrConfidence: number;
		processedAt: string;
		timings: {
			imageProcessing: number;
			grayscaleProcessing: number;
			ocrProcessing: number;
			textAnalysis: number;
			totalProcessingTime: number;
		};
	};
}

export interface CategoryMarbeteResults {
	marbeteNumber: string | null;
	metadata: {
		averageOcrConfidence: number;
		processedAt: string;
		timings: {
			imageProcessing: number;
			grayscaleProcessing: number;
			ocrProcessing: number;
			textAnalysis: number;
			totalProcessingTime: number;
		};
	};
}

export interface CategoryCarRegistrationResults {
	fullName: string;
	registrationNumber: any;
	issueDate: string | null;
	licensePlate: string;
	titleNumber: string | null;
	make: string | null;
	model: string | null;
	year: any | null;
	color: string | null;
	vin: string;
	effectiveDate: string;
	expirationDate: string;
	marbeteNumber: any | null;
	isProvisional: boolean;
	metadata: {
		averageOcrConfidence: number;
		processedAt: string;
		timings: {
			imageProcessing: number;
			imageAugmentation: number;
			ocrProcessing: number;
			textCleaning: number;
			bedrockAnalysis: number;
			resultProcessing: number;
			totalProcessingTime: number;
		};
	};
}

export interface UploadImageResponse {
	id: string;
	filename: string;
	claimId: string;
	inspectionId: string | null;
	s3Link: string;
	category: string;
	mlResults?: any;
}

export enum Categories {
	DRIVER_LICENSE = 'drivers_license',
	LICENSE_PLATE = 'license_plate',
	CAR_REGISTRATION = 'car_registration',
	VEHICLE = 'vehicle',
	ODOMETER = 'odometer',
	VIN_NUMBER = 'vin_number',
	VEHICLE_DAMAGE = 'vehicle_damage',
	VEHICLE_TRUNK = 'vehicle_trunk',
	VEHICLE_HOOD = 'vehicle_hood',
	VEHICLE_INTERIOR = 'vehicle_interior',
	MARBETE = 'marbete',
	ADDITIONAL_DOCUMENTS = 'additional_documents',
	ADDITIONAL_DRIVERS_LICENSE = 'additional_drivers_license',
	BANK_STATEMENT = 'bank_statement',
	BILL_OF_SALE = 'bill_of_sale',
	BIRTH_CERTIFICATE = 'birth_certificate',
	OTHER = 'other',
}

export enum Angles {
	left = 'Left',
	right = 'Right',
	front = 'Front',
	rear = 'Rear',
}

export default class CpeClient {
	private apiClient: AxiosInstance;

	constructor(private axios: AxiosStatic = Axios) {
		this.apiClient = this.axios.create({
			baseURL: CPE_API,
			headers: {
				Accept: 'application/json',
				Authorization: `Bearer ${CPE_API_KEY}`,
			},
		});
	}

	async getClaim(poNumber: string): Promise<ClaimResponse[]> {
		const response = await this.apiClient.get(`/v1/claims?poNumber=${poNumber}`);

		return response.data;
	}

	async getInspection(claimId: string): Promise<ClaimInspectionResponse[]> {
		const response = await this.apiClient.get(`/v1/claims/${claimId}/inspections`);

		return response.data;
	}

	async getDocumentsStatus(claimId: string): Promise<ClaimDocumentStatusResponse> {
		const response = await this.apiClient.get(`/v1/claims/${claimId}/documents/status`);

		return response.data;
	}

	async getAdditionalDocuments(claimId: string): Promise<ClaimAdditionalDocumentsResponse[]> {
		const response = await this.apiClient.get(`/v1/claims/${claimId}/additionalDocuments`);

		return response.data;
	}

	async sendImageValidation(
		images: File[],
		claimId: string,
		category: Categories,
		forceUpload: boolean
	): Promise<UploadImageResponse> {
		const formData = new FormData();
		images.forEach((image) => formData.append('images', image));
		formData.append('category', category);

		const endpoint = forceUpload ? `/v1/claims/${claimId}/images?forceUpload=true` : `/v1/claims/${claimId}/images`;

		const response = await this.apiClient.post(endpoint, formData, {
			headers: { 'content-type': 'multipart/form-data' },
		});

		return response.data;
	}

	async deleteImage(claimId: string, imageId: string): Promise<any> {
		try {
			const response = await this.apiClient.delete(`/v1/claims/${claimId}/images/${imageId}`);

			return response.data;
		} catch (error) {
			console.log('Error deleting image:', error);

			return null;
		}
	}

	async updateMLResult(
		claimId: string,
		imageId: string,
		category: string,
		ocrData?: OcrProcessingData | null
	): Promise<any> {
		try {
			if (!ocrData) return null;

			let data = {};

			if (category === 'drivers_license') {
				data = { ...data, licenseNumber: ocrData.driverLicense };
				data = { ...data, fullName: ocrData.fullName };
			}

			if (category === 'car_registration') {
				data = { ...data, licensePlate: ocrData.licensePlate };
				data = { ...data, fullName: ocrData.fullName };
				data = { ...data, make: ocrData.make };
				data = { ...data, model: ocrData.model };
				data = { ...data, year: ocrData.year };
				data = { ...data, color: ocrData.color };
			}

			if (category === 'license_plate') {
				data = { ...data, licensePlateNumber: ocrData.licensePlate };
			}

			const response = await this.apiClient.patch(`/v1/claims/${claimId}/images/${imageId}/mlResults`, {
				category: category,
				data: data,
				editor: 'virtual_inspections_app',
			});

			return response.data;
		} catch (error) {
			console.log('Error deleting image:', error);

			return null;
		}
	}

	async completeInspection(claimId: string, inspectionId: string): Promise<any> {
		try {
			const response = await this.apiClient.patch(`/v1/claims/${claimId}/inspections/${inspectionId}`, {
				inspectionDate: new Date().toISOString(),
				status: 'completed',
			});

			return response.data;
		} catch (error) {
			console.log('Error deleting image:', error);

			return null;
		}
	}
}
